// "The orchard": a grey, heavy look down at the path → tap or swipe to look up → fruit ripening in golden light → the painting.
export const orchard = {
  slug: "the-orchard",
  duration: 26,
  stages: [
    { id: "down", at: 0, hint: "a long day · tap or swipe up to look up" },
    { id: "up", at: 1.6, hint: "look up" },
    { id: "abundance", at: 6, hint: "life, ripening" },
    { id: "settle", at: 19.5, hint: "" },
  ],
  hold: { at: 1.5, need: 0.05, auto: 6 },
  touch: { kind: "press", from: 0, until: 1.6 },
  // painted panorama: looking up: fruit ripening in golden light
  layers: [
    // looking down: a grey, heavy path (walls and the ground at your feet); then the gaze lifts into the canopy
    { src: "/arcs/orchard/grey.webp", wrap: 3.97, th: 0, h: 14, y: 1.0, r: 4.9, show: [-1, 0, 2.2, 5.2] },
    { src: "/arcs/orchard/grey.webp", th: 0, y: 0.02, w: 9, h: 6.6, r: 2.2, tilt: -1.5708, show: [-1, 0, 2.2, 5.2] },
    { src: "/arcs/orchard/orchard.webp", wrap: 3.97, th: 0, h: 14, y: 3.5, r: 4.9, glow: 0.2, show: [1.2, 5, 99, 99] },
  ],
  line: { text: "However heavy the day, look up: life is abundant.", from: 8, to: 25.5, pos: "low" },
  cues: [[4.6, "rustle"], [6, "birdsong"], [9.5, "birdsong"], [12, "rustle"], [13.5, "birdsong"], [17, "birdsong"]],
  ambient: { scene: "woods", until: 99, then: [] },
  look: { start: -0.78, lift: [1.6, 4.8], end: 0.32, yaw: [[0, 0], [6, 0], [10, 0.45], [14, -0.45], [18, 0], [26, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "orchard", pos: "low" },
  speed: [[0, 1], [26, 1]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface;
varying vec3 vPos;
${"__NOISE__"}
float hash(float n){ return fract(sin(n * 127.1) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
// fruit as painted shapes: a blushing apple with a dimple, or a long mango turning gold
vec4 fruit(vec2 p, float h, float ripe){
  float brush = 0.92 + 0.08 * sin(p.x * 25.0 + p.y * 12.0 + h * 9.0);
  if (h < 0.55) {
    vec2 q = p - vec2(0.0, -0.01);
    float d = length(q / vec2(0.12, 0.105)) - 0.12 * smoothstep(0.0, 0.1, q.y) * (1.0 - smoothstep(0.0, 0.05, abs(q.x)));
    vec3 c = mix(vec3(0.45, 0.6, 0.2), vec3(0.85, 0.16, 0.14), ripe * smoothstep(-0.12, 0.1, q.x + q.y));
    return vec4(c * brush, 1.0 - smoothstep(0.92, 1.0, d));
  }
  float a = 0.6;
  vec2 q = mat2(cos(a), -sin(a), sin(a), cos(a)) * p;
  float d = length(q / vec2(0.08, 0.15 + 0.02 * q.x * 10.0));
  vec3 c = mix(vec3(0.42, 0.58, 0.2), vec3(0.98, 0.72, 0.16), ripe) + vec3(0.2, 0.0, 0.0) * ripe * smoothstep(0.0, 0.1, q.y);
  return vec4(c * brush, 1.0 - smoothstep(0.92, 1.0, d));
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.0 + 2.0) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float colourK = smoothstep(1.6, 4.8, uT);
  float settle = smoothstep(19.5, 24.5, uT);
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    float y = vPos.y;
    col = mix(vec3(0.98, 0.72, 0.38), vec3(0.88, 0.45, 0.24), smoothstep(1.0, 8.0, y)) + vec3(1.0, 0.85, 0.5) * exp(-pow(th * 1.4, 2.0)) * 0.18;
    float cw = 0.95;
    float id = floor(X / cw);
    float u = (fract(X / cw) - 0.5) * cw;
    float base = 2.4 + 0.8 * hash(id);
    float trunk = step(0.3, hash(id + 2.0)) * (1.0 - smoothstep(0.07, 0.1, abs(u + 0.06 * sin(y * 1.4 + id)))) * step(y, base + 0.6);
    col = mix(col, mix(vec3(0.28, 0.17, 0.27), vec3(0.38, 0.24, 0.32), n2), trunk);
    float band = smoothstep(2.0, 2.8, y) * (1.0 - smoothstep(7.5, 9.0, y));
    float leaf = snoise(vec3(X * 0.45, y * 0.55, 1.0)) * 0.5 + 0.5;
    float canopy = smoothstep(0.42, 0.5, leaf * band + 0.12 * band);
    float tone = snoise(vec3(X * 0.6, y * 0.7, 7.0)) * 0.5 + 0.5;
    vec3 lc = mix(vec3(0.3, 0.15, 0.34), vec3(0.24, 0.32, 0.2), smoothstep(0.3, 0.7, tone));
    lc *= 0.75 + 0.35 * smoothstep(0.45, 0.95, snoise(vec3(X * 1.6, y * 1.6, 4.0)) * 0.5 + 0.5) + 0.15 * smoothstep(2.5, 6.0, y);
    col = mix(col, lc, canopy);
    // warm light breaking through the leaves
    float ray = smoothstep(0.85, 1.0, sin((X * 0.55 + y * 0.6) * 2.5 + uT * 0.15)) * (1.0 - smoothstep(0.2, 0.9, y / 7.0));
    col += vec3(1.0, 0.8, 0.45) * ray * 0.18 * colourK;
    // fruit ripening along the branches
    vec2 fc = floor(vec2(X, y) / 0.5);
    float fh = hash2(fc);
    if (fh > 0.84 && canopy > 0.5 && y < 5.5) {
      vec2 fp = (vec2(X, y) - (fc + 0.5) * 0.5) * 0.8;
      float t0 = 6.0 + hash2(fc + 3.0) * 8.0;
      vec4 f = fruit(fp, hash2(fc + 7.0), smoothstep(t0, t0 + 2.5, uT));
      col = mix(col, f.rgb, f.a * smoothstep(5.0, 6.5, uT));
    }
    local = vec2(X, y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    float path = 1.0 - smoothstep(0.55, 0.8, abs(xz.x + 0.15 * sin(xz.y * 0.6)));
    float blades = snoise(vec3(xz.x * 9.0, xz.y * 2.5, 1.0)) * 0.5 + 0.5;
    vec3 grass = mix(vec3(0.2, 0.34, 0.14), vec3(0.32, 0.5, 0.2), n) * (0.85 + 0.25 * blades);
    vec3 stone = mix(vec3(0.5, 0.48, 0.45), vec3(0.6, 0.58, 0.54), n2);
    col = mix(grass, stone, path);
    float dap = smoothstep(0.5, 0.7, snoise(vec3(xz * 0.7, uT * 0.05)) * 0.5 + 0.5);
    col += vec3(1.0, 0.78, 0.4) * dap * 0.22 * colourK;
    local = xz;
  } else {
    float leaf = snoise(vec3(vPos.xz * 0.9, 3.0)) * 0.5 + 0.5;
    col = mix(vec3(1.0, 0.75, 0.4), mix(vec3(0.34, 0.2, 0.38), vec3(0.26, 0.33, 0.22), n2), smoothstep(0.4, 0.55, leaf));
    local = vPos.xz;
  }
  float grey = dot(col, vec3(0.3, 0.59, 0.11));
  col = mix(col, vec3(grey) * 0.72 + 0.04, (1.0 - colourK) * 0.85);
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  col = mix(col, paint, smoothstep(0.0, 0.25, settle * 1.3 - drip * 0.6 - n * 0.25));
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
